/**
 * Professional Font Validation and Management Utilities
 *
 * Provides robust validation, sanitization, and loading for brand fonts
 * with proper fallbacks and security considerations.
 */

// Common Google Fonts that are safe and widely supported
const POPULAR_GOOGLE_FONTS = new Set([
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Source Sans Pro",
  "Nunito",
  "Raleway",
  "Ubuntu",
  "Playfair Display",
  "Merriweather",
  "Oswald",
  "Lora",
  "Mukti",
  "Fira Sans",
  "PT Sans",
  "Noto Sans",
  "Work Sans",
  "Rubik",
  "Karla",
  "DM Sans",
  "Manrope",
  "Plus Jakarta Sans",
  "Outfit",
  "Space Grotesk",
  "Lexend",
])

// System fonts that don't need loading
const SYSTEM_FONTS = new Set([
  "Arial",
  "Helvetica",
  "Times New Roman",
  "Times",
  "Courier New",
  "Courier",
  "Verdana",
  "Georgia",
  "Palatino",
  "Garamond",
  "Bookman",
  "Comic Sans MS",
  "Trebuchet MS",
  "Arial Black",
  "Impact",
])

// Generic font families
const GENERIC_FAMILIES = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
])

export interface FontValidationResult {
  isValid: boolean
  sanitizedName: string
  needsLoading: boolean
  fontStack: string
  reason?: string
}

/**
 * Validates and sanitizes a font name from API
 */
export function validateBrandFont(
  fontName: string | null | undefined,
): FontValidationResult {
  // Handle null/undefined/empty cases
  if (
    !fontName ||
    typeof fontName !== "string" ||
    fontName.trim().length === 0
  ) {
    return {
      isValid: false,
      sanitizedName: "Inter",
      needsLoading: false,
      fontStack: '"Inter", sans-serif',
      reason: "Font name is empty or invalid",
    }
  }

  const trimmedFont = fontName.trim()

  // Security: Check for potentially malicious content
  if (containsMaliciousContent(trimmedFont)) {
    return {
      isValid: false,
      sanitizedName: "Inter",
      needsLoading: false,
      fontStack: '"Inter", sans-serif',
      reason: "Font name contains potentially unsafe characters",
    }
  }

  // Sanitize the font name
  const sanitizedName = sanitizeFontName(trimmedFont)

  // Check if it's a generic family
  if (GENERIC_FAMILIES.has(sanitizedName.toLowerCase())) {
    return {
      isValid: true,
      sanitizedName,
      needsLoading: false,
      fontStack: `${sanitizedName}, sans-serif`,
      reason: "Generic font family",
    }
  }

  // Check if it's a system font
  if (SYSTEM_FONTS.has(sanitizedName)) {
    return {
      isValid: true,
      sanitizedName,
      needsLoading: false,
      fontStack: `"${sanitizedName}", sans-serif`,
      reason: "System font",
    }
  }

  // Check if it's a popular Google Font
  if (POPULAR_GOOGLE_FONTS.has(sanitizedName)) {
    return {
      isValid: true,
      sanitizedName,
      needsLoading: true,
      fontStack: `"${sanitizedName}", "Inter", sans-serif`,
      reason: "Popular Google Font",
    }
  }

  // For other fonts, assume they might be Google Fonts but validate the name
  if (isValidGoogleFontName(sanitizedName)) {
    return {
      isValid: true,
      sanitizedName,
      needsLoading: true,
      fontStack: `"${sanitizedName}", "Inter", sans-serif`,
      reason: "Potentially valid Google Font",
    }
  }

  // Invalid font - fallback to Inter
  return {
    isValid: false,
    sanitizedName: "Inter",
    needsLoading: false,
    fontStack: '"Inter", sans-serif',
    reason: "Font name does not match expected patterns",
  }
}

/**
 * Checks for potentially malicious content in font names
 */
function containsMaliciousContent(fontName: string): boolean {
  // Check for common injection patterns
  const maliciousPatterns = [
    /<script/i,
    /javascript:/i,
    /data:/i,
    /vbscript:/i,
    /on\w+=/i,
    /expression\(/i,
    /url\(/i,
    /import/i,
    /@/,
    /;/,
    /{/,
    /}/,
  ]

  return maliciousPatterns.some((pattern) => pattern.test(fontName))
}

/**
 * Sanitizes font name by removing unsafe characters
 */
function sanitizeFontName(fontName: string): string {
  // Remove quotes and normalize whitespace
  return fontName.replace(/['"]/g, "").replace(/\s+/g, " ").trim()
}

/**
 * Validates if a font name could be a valid Google Font
 */
function isValidGoogleFontName(fontName: string): boolean {
  // Google Font names typically:
  // - Are 1-50 characters long
  // - Contain only letters, numbers, spaces, and hyphens
  // - Don't start or end with spaces or hyphens
  // - Don't contain consecutive spaces or hyphens

  if (fontName.length === 0 || fontName.length > 50) {
    return false
  }

  // Check for valid characters only
  if (!/^[a-zA-Z0-9\s-]+$/.test(fontName)) {
    return false
  }

  // Check for proper start/end
  if (/^[\s-]|[\s-]$/.test(fontName)) {
    return false
  }

  // Check for consecutive spaces or hyphens
  if (/[\s-]{2,}/.test(fontName)) {
    return false
  }

  return true
}

/**
 * Generates Google Fonts URL for a given font name
 */
export function generateGoogleFontUrl(
  fontName: string,
  weights: number[] = [300, 400, 500, 600, 700],
): string {
  const encodedName = encodeURIComponent(fontName).replace(/%20/g, "+")
  const weightString = weights.join(";")
  return `https://fonts.googleapis.com/css2?family=${encodedName}:wght@${weightString}&display=swap`
}

/**
 * Checks if a Google Font link already exists in the document
 */
export function fontLinkExists(fontName: string): boolean {
  const encodedName = encodeURIComponent(fontName).replace(/%20/g, "+")
  return !!document.querySelector(`link[href*="${encodedName}"]`)
}

/**
 * Creates and loads a Google Font link element
 */
export function loadGoogleFont(
  fontName: string,
  weights?: number[],
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (fontLinkExists(fontName)) {
      resolve()
      return
    }

    const link = document.createElement("link")
    link.href = generateGoogleFontUrl(fontName, weights)
    link.rel = "stylesheet"

    // Add load/error handlers
    link.onload = () => resolve()
    link.onerror = () => reject(new Error(`Failed to load font: ${fontName}`))

    // Add to document head
    document.head.appendChild(link)

    // Fallback timeout (5 seconds)
    setTimeout(() => {
      reject(new Error(`Font loading timeout: ${fontName}`))
    }, 5000)
  })
}

/**
 * Preloads a font for better performance
 */
export function preloadFont(fontName: string, weights?: number[]): void {
  if (fontLinkExists(fontName)) return

  const link = document.createElement("link")
  link.href = generateGoogleFontUrl(fontName, weights)
  link.rel = "preload"
  link.as = "style"
  link.onload = () => {
    // Convert to stylesheet after preload
    link.rel = "stylesheet"
  }

  document.head.appendChild(link)
}
